import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { observer } from "mobx-react";
import {
  InvoiceFilters,
  parseQueryParamsForInvoiceFilters,
} from "../../../models/Invoice/InvoiceFilters";
import { SortType } from "../../../models/SortType";
import InvoicesTable from "../../components/invoices/InvoicesTable";
import { useNotification } from "../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import FormTextField from "../../components/common/form/FormTextField";
import AppButton from "../../components/common/buttons/AppButton";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import CustomTypography from "../../components/common/form/CustomTypography";

const InvoicesListing: React.FC = () => {
  const theme = useTheme();
  const { invoiceStore, userStore } = useStores();

  const { showError } = useNotification();

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const initialFilters: InvoiceFilters = parseQueryParamsForInvoiceFilters(
    null,
    searchParams,
  );

  const [filters, setFilters] = useState<InvoiceFilters>(initialFilters);

  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || "",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  useEffect(() => {
    if (
      userStore.hasPermission([
        EmployeeRoles.EDIT_INVOICE,
        EmployeeRoles.DOWNLOAD_INVOICE_AND_PI,
        EmployeeRoles.CREATE_INVOICE_AND_PI,
      ])
    ) {
      setIsLoading(true);
      invoiceStore
        .getInvoices(filters)
        .catch((err) =>
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          ),
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    const updateQueryParams = () => {
      const searchParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          const paramValue = Array.isArray(value)
            ? value.join(",")
            : value?.toString() || "";
          searchParams.set(key, paramValue);
        }
      });

      const queryString = searchParams.toString();
      if (queryString) {
        navigate({ search: queryString }, { replace: true });
      }
    };

    updateQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, navigate]);

  const handleSearchBar = async (searchText: string | undefined) => {
    if (searchText && searchText.length !== 0) {
      setSearchTerm(searchText);
    } else {
      setSearchTerm("");
      setFilters((prev) => ({
        ...prev,
        search_text: "",
      }));
    }
  };

  const handleSearchSubmit = () => {
    setFilters((prev) => ({
      ...prev,
      search_text: searchTerm,
      page_no: 1,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      if (searchTerm.trim().length > 0) {
        setFilters((prev) => ({
          ...prev,
          search_text: searchTerm,
          page_no: 1,
        }));
      } else {
        setSearchTerm("");
        setFilters((prev) => ({
          ...prev,
          search_text: "",
          page_no: 1,
        }));
      }
    }
  };

  const resetSearch = () => {
    setSearchTerm("");
    setFilters({
      page_no: 1,
      limit: 30,
      sort_by: "created_date",
      sort_type: SortType.DESC,
    });
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setFilters((prev) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setFilters((prev) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const ordersPageInnerSx = {
    marginTop: "0.6rem",
    paddingX: 4,
    paddingY: 2,
    boxShadow:
      "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
    borderRadius: "4px",
    minHeight: { xs: "60vh", md: window.innerHeight - 110 },
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      {!userStore.hasPermission([
        EmployeeRoles.EDIT_INVOICE,
        EmployeeRoles.DOWNLOAD_INVOICE_AND_PI,
        EmployeeRoles.CREATE_INVOICE_AND_PI,
      ]) && (
        <Paper>
          <Box sx={ordersPageInnerSx}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.4,
                mb: 2,
              }}
            >
              <CustomTypography variant="heading3">Invoices</CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View and manage invoices. Search by customer name, order ID, or
                invoice number.
              </CustomTypography>
            </Box>
            <PermissionBlockLabel />
          </Box>
        </Paper>
      )}
      {userStore.hasPermission([
        EmployeeRoles.EDIT_INVOICE,
        EmployeeRoles.DOWNLOAD_INVOICE_AND_PI,
        EmployeeRoles.CREATE_INVOICE_AND_PI,
      ]) && (
        <Paper>
          <Box sx={ordersPageInnerSx}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.5,
              }}
            >
              <CustomTypography variant="heading3">Invoices</CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View and manage invoices. Search by customer name, order ID, or
                invoice number.
              </CustomTypography>
            </Box>

            <Paper
              variant="outlined"
              sx={{
                mt: 2,
                borderRadius: 2,
                borderColor: "divider",
                overflow: "hidden",
              }}
            >
              <Box
                onClick={() => setIsSearchExpanded(!isSearchExpanded)}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 0.5,
                  cursor: "pointer",
                  borderBottom: isSearchExpanded ? "1px solid" : "none",
                  borderColor: "divider",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <CustomTypography variant="heading6">Search</CustomTypography>
                <IconButton size="small">
                  {isSearchExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={isSearchExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 1.5,
                    }}
                  >
                    <FormTextField
                      id="invoice-search"
                      placeholder="Search by customer name, order ID, or invoice number"
                      type="search"
                      variant="outlined"
                      value={searchTerm}
                      onChange={(_, value) => handleSearchBar(value)}
                      onKeyDown={(e) => handleKeyDown(e)}
                    />
                    <AppButton
                      variant="primary"
                      label="Search"
                      onClick={handleSearchSubmit}
                      customStyles={{ minWidth: 140, height: 40 }}
                    />
                    <AppButton
                      variant="transparentPrimary"
                      label="Reset Search"
                      disabled={!searchTerm || searchTerm === ""}
                      onClick={resetSearch}
                      customStyles={{ minWidth: 140, height: 40 }}
                    />
                  </Box>
                </Box>
              </Collapse>
            </Paper>

            {isLoading && (
              <Box
                sx={{
                  minHeight: "40vh",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress />
              </Box>
            )}

            {!isLoading && (
              <Box mt={3}>
                {invoiceStore.invoices.length === 0 && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="10rem"
                    textAlign="center"
                    border="1px dashed"
                    borderColor="divider"
                    borderRadius={2}
                  >
                    <CustomTypography
                      variant="heading6"
                      fontWeight={500}
                      color={theme.palette.customColors?.greyTextColor}
                    >
                      No invoices match the current search criteria.
                    </CustomTypography>
                  </Box>
                )}
                {invoiceStore.invoices.length > 0 && (
                  <InvoicesTable
                    maxHeight={window.innerHeight - 350}
                    invoices={invoiceStore.invoices}
                    limit={invoiceStore.limit}
                    page_no={filters.page_no}
                    total_count={invoiceStore.total_count}
                    total_pages={invoiceStore.total_pages}
                    sortBy={filters.sort_by}
                    sortType={filters.sort_type}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    onSortChange={(column, sortType) => {
                      setFilters((prev) => ({
                        ...prev,
                        sort_by: column,
                        sort_type: sortType,
                      }));
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
        </Paper>
      )}
    </Grid2>
  );
};

export default observer(InvoicesListing);
